// Blog content - 15 original articles across 3 categories.
// All article copy is original educational content written for this site.

export const BLOG_CATEGORIES = [
  { id: "all", label: "All Articles" },
  { id: "wellness", label: "Holistic Health & Wellness" },
  { id: "meditation", label: "Meditation" },
  { id: "healing", label: "Healing & Empowerment" },
];

export const CATEGORY_META = {
  wellness: { label: "Holistic Health & Wellness", color: "#1e9a92" },
  meditation: { label: "Meditation", color: "#5a67a8" },
  healing: { label: "Healing & Empowerment", color: "#5b7d8c" },
};

const P = (text) => ({ type: "p", text });
const H = (text) => ({ type: "h", text });
const L = (items) => ({ type: "list", items });
const Q = (text) => ({ type: "q", text });

export const BLOG_POSTS = [
  // ================= HOLISTIC HEALTH & WELLNESS =================
  {
    slug: "bio-individuality-no-one-size-fits-all",
    category: "wellness",
    title: "Bio Individuality: Why There's No One Size Fits All Diet",
    excerpt:
      "The diet that transforms one person can leave another exhausted. Discover the principle of bio individuality and why your body deserves a personalized approach to nourishment.",
    image:
      "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=1200&h=800&q=80",
    date: "July 20, 2026",
    readTime: "6 min read",
    content: [
      P("Everyone's body is different and has unique nutritional requirements. These needs are shaped by differences in anatomy, metabolism, body composition, and even cell structure. This is the heart of bio individuality: the understanding that there is no single perfect diet that works for every person."),
      H("Why the latest trend may not be your answer"),
      P("A way of eating that leaves your friend glowing with energy might leave you sluggish or bloated. That is not a failure of willpower, it is biochemistry. Age, ancestry, activity level, stress, sleep, and even the season all influence what your body truly needs to thrive."),
      H("Listening to your body's signals"),
      P("Holistic nutrition begins with curiosity rather than rules. Instead of asking 'Is this food good or bad?', we ask 'How does this food make me feel, in my energy, digestion, mood, and sleep?' Over time, these gentle observations become a reliable inner compass."),
      H("Building your personalized foundation"),
      L([
        "Favor whole, minimally processed foods most of the time.",
        "Notice which meals leave you energized versus drained, and keep a simple log.",
        "Stay hydrated and prioritize rest, nutrition never works in isolation.",
        "Adjust with the seasons and with your changing life circumstances.",
      ]),
      P("Honoring bio individuality is an act of self respect. When we stop chasing someone else's rules and start partnering with our own body, nourishment becomes sustainable, enjoyable, and deeply personal."),
      P("This article is for educational purposes and is not a substitute for personalized medical or nutritional advice."),
    ],
  },
  {
    slug: "art-of-mindful-eating",
    category: "wellness",
    title: "The Art of Mindful Eating: Nourishing Body and Soul",
    excerpt:
      "Mindful eating transforms meals from a rushed necessity into a nourishing ritual. Learn simple practices to slow down, savor, and reconnect with your body's wisdom.",
    image:
      "https://images.unsplash.com/photo-1498837167922-ddd27525d352?auto=format&fit=crop&w=1200&h=800&q=80",
    date: "July 5, 2026",
    readTime: "5 min read",
    content: [
      P("So many of us eat on autopilot, standing at the counter, scrolling a screen, or racing between tasks. Mindful eating gently reverses this habit, inviting us to bring full, kind attention to the experience of nourishing ourselves."),
      H("What mindful eating actually means"),
      P("Mindful eating is simply paying attention, on purpose and without judgment, to the food in front of you and the signals within your body. It is not a diet and it forbids nothing. Instead, it restores the natural conversation between you and your hunger, fullness, and satisfaction."),
      H("The benefits ripple outward"),
      P("When we slow down, digestion often improves, we tend to feel satisfied with appropriate portions, and eating becomes a source of genuine pleasure rather than guilt. Many people also notice that a calmer relationship with food quiets the stress that once surrounded mealtimes."),
      H("Simple practices to begin today"),
      L([
        "Take three slow breaths before your first bite to settle your nervous system.",
        "Put your fork down between bites and notice flavor, texture, and aroma.",
        "Check in halfway through: Am I still hungry, or simply eating out of habit?",
        "Offer a moment of gratitude for the nourishment on your plate.",
      ]),
      P("You do not need to eat every meal this way. Even one mindful meal a day can reawaken your body's innate wisdom and turn eating back into the nourishing, grounding ritual it was always meant to be."),
    ],
  },
  {
    slug: "meditation-for-everyday-calm",
    category: "wellness",
    title: "Meditation for Everyday Calm: Simple Practices to Begin",
    excerpt:
      "You don't need hours of silence or a mountaintop to meditate. Discover beginner friendly practices that fit real life and help you stay calm, focused, and balanced.",
    image: "/meditation/everyday-calm-group.webp",
    date: "June 22, 2026",
    readTime: "5 min read",
    content: [
      P("Meditation is one of the most accessible tools for staying calm, focused, and balanced, yet many people abandon it before they begin, convinced they 'can't stop thinking.' Here is the good news: the goal was never to empty your mind. It is simply to notice when it wanders and gently return."),
      H("Why a busy mind is normal"),
      P("Thoughts are what minds do, the way waves are what oceans do. Meditation is not about stopping the waves; it is about learning to rest on the shore and watch them come and go without being swept away. Every time you notice you have drifted and come back, that is the practice working."),
      H("Three practices to try"),
      L([
        "Breath awareness, Sit comfortably and follow the natural rhythm of your breath for two minutes.",
        "Body scan, Slowly move your attention from your head to your toes, softening tension along the way.",
        "Loving kindness, Silently wish yourself and others peace, ease, and well being.",
      ]),
      H("Making it stick"),
      P("Start absurdly small, even two minutes a day is enough to build the habit. Anchor it to something you already do, like your morning tea or before you fall asleep. Consistency matters far more than duration; a little every day outperforms an occasional marathon."),
      P("Over time, this quiet daily pause becomes a refuge, a place to return to whenever life feels overwhelming, and a foundation for clearer thinking and steadier emotions."),
    ],
  },
  {
    slug: "essential-oils-101-aromatherapy",
    category: "wellness",
    title: "Essential Oils 101: Aromatherapy for Emotional Balance",
    excerpt:
      "The scent of a single drop can shift your entire mood. Explore how essential oils work through the body, and how to use them safely for calm, focus, and emotional balance.",
    image: "/wellness/essential-oils.webp",
    date: "June 15, 2026",
    readTime: "6 min read",
    content: [
      P("Inhaling essential oils can influence the body through several systems and pathways. When we breathe in through the nose, aromatic molecules interact with the olfactory organs and travel almost instantly to the brain, where they can gently affect mood, memory, and our sense of calm."),
      H("How aromatherapy reaches you"),
      P("The olfactory system is intimately connected with the brain's emotional centers. This is why a familiar scent can transport you to a memory in a heartbeat. Molecules inhaled through the nose also reach the lungs and interact with the respiratory system, making breath and aroma powerful partners in relaxation."),
      H("A few gentle favorites"),
      L([
        "Lavender, a classic companion for winding down and restful sleep.",
        "Peppermint, bright and clarifying, often used for focus and freshness.",
        "Citrus oils like sweet orange, uplifting and cheerful for low moods.",
        "Frankincense, grounding and centering, wonderful for meditation.",
      ]),
      H("Using essential oils safely"),
      P("Essential oils are highly concentrated, so a little goes a long way. Diffuse a few drops into the air, or dilute properly in a carrier oil before applying to the skin. Learn how to store and purchase quality oils, keep them away from eyes, and always check for sensitivities, especially during pregnancy or with children and pets."),
      P("Paired with breathwork, meditation, and simple intuitive techniques, essential oils become a beautiful, drug free way to help maintain calm, clear thoughts and stay relaxed through whatever challenges life offers."),
    ],
  },
  {
    slug: "gentle-movement-yoga-taichi-qigong",
    category: "wellness",
    title: "Gentle Movement: Yoga, Tai Chi & Qi Gong for Whole Body Vitality",
    excerpt:
      "Movement doesn't have to be intense to be transformative. Discover how gentle, mindful practices build strength, calm the mind, and restore energy at any age.",
    image:
      "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&w=1200&h=800&q=80",
    date: "June 2, 2026",
    readTime: "5 min read",
    content: [
      P("We often think exercise must be hard to be worthwhile. Yet some of the most healing practices in the world are slow, gentle, and deeply mindful. Yoga, Tai Chi, and Qi Gong prove that movement aligned with the breath can build real strength while calming the mind and restoring energy."),
      H("Yoga, uniting breath and body"),
      P("Yoga blends mindful movement, breath, and stillness to improve flexibility, balance, and inner calm. Gentle styles meet you exactly where you are, making it accessible whether you are brand new or returning after years away."),
      H("Tai Chi, meditation in motion"),
      P("Often described as 'meditation in motion,' Tai Chi flows through slow, graceful sequences that improve balance, coordination, and focus. Its unhurried pace makes it especially supportive for stress relief and gentle daily practice."),
      H("Qi Gong, cultivating life energy"),
      P("Qi Gong combines flowing movement, breath, and intention to cultivate and circulate 'qi,' or life energy. Even a few minutes can leave you feeling clearer, calmer, and more centered in your body."),
      H("Finding your rhythm"),
      L([
        "Begin with just ten minutes and let the practice grow naturally.",
        "Move within a comfortable range, gentle does not mean ineffective.",
        "Let the breath lead the movement rather than the other way around.",
        "Choose the practice that brings you joy; consistency follows delight.",
      ]),
      P("These practices remind us that vitality is not about pushing harder, but about moving with awareness, kindness, and breath, a form of movement you can enjoy for a lifetime."),
    ],
  },

  // ================= MEDITATION (Beginner -> Master) =================
  {
    slug: "meditation-for-beginners-first-steps",
    category: "meditation",
    title: "Meditation for Beginners: Your First Steps to Stillness",
    excerpt:
      "Never meditated before? This gentle beginner's guide covers posture, breath, and the one mindset shift that makes all the difference when you're starting out.",
    image:
      "https://images.unsplash.com/photo-1602192509154-0b900ee1f851?auto=format&fit=crop&w=1200&h=800&q=80",
    date: "August 8, 2026",
    readTime: "6 min read",
    content: [
      P("If you've ever thought 'I could never meditate, my mind is too busy,' this article is for you. The truth is that a busy mind doesn't disqualify you from meditating; it's the very reason to begin. Meditation is not about achieving a blank mind. It's about learning to be present with whatever is already here."),
      H("Level 1: The beginner's mindset"),
      P("The single most helpful shift for a beginner is this: the goal is not to stop thinking, but to notice when you've drifted and gently return. Every time you catch your mind wandering and come back to your anchor, that is one 'rep', the practice working exactly as intended. There is no failing at meditation."),
      H("Finding your posture"),
      L([
        "Sit comfortably, on a chair with feet flat, or cross legged on a cushion.",
        "Let your spine be tall but not rigid, like a stack of coins.",
        "Rest your hands on your thighs and softly close your eyes or lower your gaze.",
        "Relax your shoulders, jaw, and the space between your eyebrows.",
      ]),
      H("Your first practice: breath awareness"),
      P("Bring gentle attention to the natural rhythm of your breath, the cool air in, the warm air out. You don't need to control it; simply feel it. When your mind wanders (and it will), kindly escort it back to the breath. Start with just two to five minutes."),
      H("Making it a habit"),
      P("Consistency matters far more than duration. Anchor your practice to something you already do, your morning coffee, or the moment before sleep. A few minutes every day builds the foundation for everything that follows. Be patient and kind with yourself; you are learning a skill that unfolds over a lifetime."),
      Q("You don't have to control your thoughts. You just have to stop letting them control you."),
    ],
  },
  {
    slug: "meditation-intermediate-building-daily-practice",
    category: "meditation",
    title: "Building a Daily Practice: The Intermediate Meditator",
    excerpt:
      "You've got the basics down. Now learn to steady your practice, work skillfully with distraction and restlessness, and explore techniques beyond the breath.",
    image: "/meditation/group-meditation.png",
    date: "July 26, 2026",
    readTime: "7 min read",
    content: [
      P("Once meditation becomes a familiar ritual, a new stage begins. The intermediate meditator has moved past 'How do I do this?' and into 'How do I go deeper, and stay consistent even when it's hard?' This is where the real richness of practice opens up."),
      H("Level 2: Steadying the practice"),
      P("At this stage, gently lengthen your sits, perhaps ten to twenty minutes, and aim for the same time and place each day. A stable routine signals to the mind that this is simply what you do now, reducing the daily negotiation of whether to practice."),
      H("Working with the 'hindrances'"),
      P("Every meditator meets recurring visitors: restlessness, drowsiness, doubt, craving, and aversion. Rather than fighting them, the intermediate practitioner learns to name them ('ah, restlessness') and observe them with curiosity. What you can name, you can hold with more spaciousness, and it loosens its grip."),
      H("Expanding your toolkit"),
      L([
        "Body scan, moving attention slowly through the body to deepen embodiment.",
        "Loving kindness (metta), cultivating warmth toward yourself and others.",
        "Noting, silently labeling experiences ('thinking,' 'hearing,' 'feeling').",
        "Walking meditation, bringing the same attention to slow, deliberate steps.",
      ]),
      H("From technique to relationship"),
      P("As practice matures, it stops feeling like a task and becomes a relationship, a daily meeting with your own mind. You begin to notice the benefits spilling into daily life: a longer pause before reacting, more patience, and a quiet steadiness that stays with you off the cushion."),
      P("Note: This article offers general guidance for personal practice and is not a substitute for instruction from a qualified teacher."),
    ],
  },
  {
    slug: "meditation-advanced-deepening-your-practice",
    category: "meditation",
    title: "Deepening Your Practice: Advanced Meditation Techniques",
    excerpt:
      "For the seasoned meditator ready to go further, explore concentration (samatha), insight (vipassana), longer sits, and the subtle art of working with the mind's depths.",
    image: "/meditation/solo-meditation.png",
    date: "July 14, 2026",
    readTime: "8 min read",
    content: [
      P("For those who have practiced steadily for months or years, meditation reveals deeper territory. The advanced practitioner is less interested in techniques as tools and more curious about the nature of the mind itself. This is subtle, rewarding work, best supported by an experienced teacher."),
      H("Level 3: Concentration and insight"),
      P("Two great streams run through most contemplative traditions. Concentration practices (often called samatha) cultivate a steady, unified attention that can settle into deep states of calm and absorption. Insight practices (vipassana) turn that stabilized attention toward the changing nature of experience itself, revealing how thoughts, sensations, and emotions arise and pass away."),
      H("Extending the sit"),
      P("Advanced practice often involves longer sessions, forty five minutes to an hour, and periodic retreats where practice deepens through sustained silence. In longer sits, the mind moves through waves of restlessness and settling; learning to stay present through both is where profound stability is forged."),
      H("Subtle skills to explore"),
      L([
        "Cultivating equanimity, a balanced, non reactive presence with pleasant and unpleasant alike.",
        "Investigating the 'three characteristics', impermanence, unsatisfactoriness, and non self.",
        "Resting in open awareness, letting attention be spacious rather than fixed on one object.",
        "Working with subtle energy and stillness that arise in deep concentration.",
      ]),
      H("A word of guidance"),
      P("Deep practice can surface old emotions and unexpected experiences. This is normal, but it is precisely why a trusted teacher or community becomes invaluable at this stage, to offer context, reassurance, and skillful guidance. Progress here is measured not in dramatic experiences, but in how you meet ordinary life with growing wisdom and ease."),
      Q("The deeper you go, the simpler it becomes: just this breath, just this moment, just this."),
    ],
  },
  {
    slug: "meditation-mastery-living-meditation",
    category: "meditation",
    title: "The Path to Mastery: Living Meditation",
    excerpt:
      "Mastery isn't a destination or a special state, it's a way of living. Discover how the deepest practice dissolves the line between the cushion and everyday life.",
    image:
      "https://images.unsplash.com/photo-1643682661119-28da0685be2c?auto=format&fit=crop&w=1200&h=800&q=80",
    date: "June 30, 2026",
    readTime: "7 min read",
    content: [
      P("What does 'mastery' in meditation truly mean? It is not the ability to sit for hours in blissful states, nor a collection of extraordinary experiences. In the deepest sense, mastery is when meditation is no longer something you do, it is who you are. The practice and the practitioner become one."),
      H("Level 4: From practice to presence"),
      P("For the master, formal sitting continues, but its fruits ripen everywhere: washing dishes, listening to a friend, waiting in traffic. Presence is no longer confined to the cushion. Every moment becomes an invitation to meet life fully, without the constant commentary of a restless mind."),
      H("The hallmarks of a mature practice"),
      L([
        "Equanimity, remaining steady and openhearted through life's ups and downs.",
        "Compassion, a natural warmth toward oneself and all beings.",
        "Non attachment, holding experiences lightly, without grasping or pushing away.",
        "Simplicity, a return to the ordinary, seen with fresh and grateful eyes.",
      ]),
      H("Giving it away"),
      P("A beautiful sign of maturity is the desire to serve. Many long time practitioners feel called to share what they've found, mentoring beginners, supporting a community, or simply embodying peace in a way that quietly uplifts everyone around them. Wisdom, when it ripens, naturally overflows."),
      H("The endless beginning"),
      P("Perhaps the greatest secret of mastery is humility: the master returns, again and again, to the beginner's mind, curious, open, and free of the assumption that there is nothing left to learn. In this sense the path has no final summit. There is only this breath, this moment, met with love, over and over, for a lifetime."),
      Q("In the beginner's mind there are many possibilities; in the expert's mind there are few."),
      P("Note: This article is a reflection on contemplative practice for inspiration and is not a substitute for guidance from an experienced teacher."),
    ],
  },

  // ================= HEALING & EMPOWERMENT =================
  {
    slug: "recognizing-domestic-abuse-not-always-physical",
    category: "healing",
    title: "Recognizing Domestic Abuse: It's Not Always Physical",
    excerpt:
      "Abuse can be emotional, financial, and psychological long before it is ever physical. Understanding the many forms of control can help you or someone you love find safety.",
    image:
      "https://images.pexels.com/photos/8430310/pexels-photo-8430310.jpeg?auto=compress&cs=tinysrgb&w=1200&h=800&fit=crop",
    date: "July 22, 2026",
    readTime: "8 min read",
    content: [
      P("When we hear 'domestic abuse,' many of us picture physical harm. But abuse, sometimes called domestic violence (DV) or intimate partner violence, often begins and persists in ways that leave no visible marks. Understanding the full picture can be life changing, and even life saving."),
      H("The many forms abuse can take"),
      L([
        "Emotional and verbal, constant criticism, humiliation, name calling, or contempt",
        "Psychological, intimidation, threats, isolation from friends and family",
        "Financial, controlling money, restricting access, or sabotaging your work",
        "Coercive control, monitoring, dictating your choices, and eroding your independence",
        "Physical and sexual, any unwanted or forced contact, or the threat of it",
      ]),
      H("Warning signs within a relationship"),
      P("Abuse is fundamentally about power and control. You might notice a partner who isolates you from loved ones, who swings between charm and cruelty, who blames you for their behavior, or who makes you feel afraid, small, or 'crazy.' If you find yourself constantly walking on eggshells, that instinct deserves to be honored, not dismissed."),
      H("It is never your fault"),
      P("Abuse is a choice made by the person who harms, never a reflection of anything the person being harmed did or didn't do. Leaving or seeking help can be complicated and, at times, dangerous, which is why safety planning with trained advocates matters so much."),
      H("Reaching out for support"),
      P("You do not have to navigate this alone. Confidential, judgment free help is available around the clock. In the U.S., the National Domestic Violence Hotline is available 24/7 at 1-800-799-7233 (SAFE), or you can text START to 88788. In an emergency, always call 911. Advocates can help with safety planning, resources, and simply listening."),
      Q("Reaching out is not weakness. It is one of the bravest, most self honoring things a person can do."),
      P("Note: This article offers general education and support, not emergency or legal advice. If you are in immediate danger, please contact local emergency services right away."),
    ],
  },
  {
    slug: "healing-after-abuse-reclaiming-your-worth",
    category: "healing",
    title: "Healing After Abuse: Reclaiming Your Worth & Well Being",
    excerpt:
      "Healing is not linear, and it is deeply personal. Explore gentle, holistic ways to rebuild safety, self trust, and joy after emotional or narcissistic abuse.",
    image:
      "https://images.unsplash.com/photo-1607748862156-7c548e7e98f4?auto=format&fit=crop&w=1200&h=800&q=80",
    date: "July 12, 2026",
    readTime: "7 min read",
    content: [
      P("Leaving or surviving an abusive relationship is an enormous act of courage, and it is also the beginning of a journey, not the end. Healing after emotional, psychological, or narcissistic abuse is deeply personal, rarely linear, and absolutely possible. This is a season for extraordinary gentleness with yourself."),
      H("Give your nervous system time to settle"),
      P("Prolonged stress and hypervigilance take a real toll on the body. In the early stages, simply feeling safe is the work. Grounding practices, slow breathing, feeling your feet on the floor, time in nature, and consistent sleep, gently signal to your body that the danger has passed."),
      H("Rebuilding self trust"),
      P("Manipulation often teaches us to doubt our own perceptions. Rebuilding trust in yourself means slowly relearning that your feelings are valid and your instincts are wise. Journaling, affirmations, and noticing small daily choices ('I wanted tea, so I made tea') help restore your sense of agency."),
      H("Holistic tools that support healing"),
      L([
        "Mind body practices like meditation, gentle yoga, and breathwork to release stored stress",
        "Emotional Freedom Technique (EFT) to help process difficult emotions",
        "Nourishing food, hydration, and rest to rebuild physical resilience",
        "A supportive community and, when possible, a trauma informed therapist or coach",
      ]),
      H("Rediscovering joy and purpose"),
      P("As safety and self trust return, there is space for something beautiful: rediscovering who you are outside of survival. Reconnecting with creativity, old passions, and new dreams is not frivolous, it is a vital part of reclaiming your life. You are allowed to want joy again."),
      Q("You are not what happened to you. You are who you choose to become, and that story is still being written."),
      P("Note: This article offers holistic, educational support and is not a replacement for professional trauma care. In the U.S., confidential help is available 24/7 via the National Domestic Violence Hotline at 1-800-799-7233 or by texting START to 88788."),
    ],
  },
];

// Author byline shown at the end of every article
export const AUTHOR = {
  name: "Kamla Das",
  role: "Certified PEMF, Red Light & Holistic Health Care Practitioner, Author & Astrologer",
  photo:
    "https://s3-us-west-2.amazonaws.com/sticky-folio-uploads/res3000/221450/6af011e0-9a98-4ec9-8ad9-42461f728ec0.jpg",
  bio: "Kamla Das is a Certified Holistic Health Care Practitioner, Author, and devoted Mother. Blending Eastern and Western modalities, she guides clients toward a healthy mind, body, and soul, with a dedicated focus on nutrition, wellness, energy healing, and astrology.",
};

// Tags per article (used for search + tag chips)
const TAGS = {
  "bio-individuality-no-one-size-fits-all": ["Nutrition", "Bio Individuality", "Whole Foods"],
  "art-of-mindful-eating": ["Mindful Eating", "Nutrition", "Digestion", "Self Care"],
  "meditation-for-everyday-calm": ["Meditation", "Stress Relief", "Mindfulness"],
  "essential-oils-101-aromatherapy": ["Essential Oils", "Aromatherapy", "Relaxation"],
  "gentle-movement-yoga-taichi-qigong": ["Yoga", "Tai Chi", "Qi Gong", "Movement"],
  "meditation-for-beginners-first-steps": ["Meditation", "Beginner", "Breath", "Mindfulness"],
  "meditation-intermediate-building-daily-practice": ["Meditation", "Daily Practice", "Metta", "Focus"],
  "meditation-advanced-deepening-your-practice": ["Meditation", "Vipassana", "Samatha", "Advanced"],
  "meditation-mastery-living-meditation": ["Meditation", "Mastery", "Presence", "Equanimity"],
  "recognizing-domestic-abuse-not-always-physical": ["Domestic Abuse", "DV", "Coercive Control", "Safety"],
  "healing-after-abuse-reclaiming-your-worth": ["Healing", "Recovery", "Self Worth", "Empowerment"],
};

// attach tags to each post
BLOG_POSTS.forEach((p) => {
  p.tags = TAGS[p.slug] || [];
});

// ---- Meditation series: ordering + short guided audio scripts ----
const MED_PARTS = {
  "meditation-for-beginners-first-steps": 1,
  "meditation-intermediate-building-daily-practice": 2,
  "meditation-advanced-deepening-your-practice": 3,
  "meditation-mastery-living-meditation": 4,
};

// Short, original guided-meditation scripts read aloud by the in-app player.
const MED_SCRIPTS = {
  "meditation-for-beginners-first-steps": {
    label: "3-minute guided practice",
    lines: [
      "Welcome. Find a comfortable seat, and let your hands rest gently.",
      "Softly close your eyes, or lower your gaze toward the floor.",
      "Take a slow breath in through your nose... and gently let it go.",
      "Let your shoulders soften. Let your jaw relax.",
      "Now simply notice your natural breath. You don't need to change it.",
      "Feel the cool air as you breathe in... and the warm air as you breathe out.",
      "If your mind wanders, that's perfectly okay. Kindly guide it back to the breath.",
      "Rest here for a few quiet breaths, right in this moment.",
      "When you're ready, slowly open your eyes. Beautifully done.",
    ],
  },
  "meditation-intermediate-building-daily-practice": {
    label: "4-minute guided practice",
    lines: [
      "Settle into your seat, spine tall, body at ease.",
      "Take three slow, deliberate breaths to arrive fully here.",
      "Bring your attention to the breath, resting it there like a feather.",
      "Now gently scan your body, from the crown of your head down to your feet.",
      "Notice any tension, and with each out breath, allow it to soften.",
      "If a thought or feeling arises, quietly name it: thinking... or feeling.",
      "Then, without judgment, return your attention to the breath.",
      "Let yourself simply be with what is, moment after moment.",
      "Take one more deep breath, and gently return to the room.",
    ],
  },
  "meditation-advanced-deepening-your-practice": {
    label: "5-minute guided practice",
    lines: [
      "Find your posture. Let stillness settle over the body like dusk.",
      "Rest your full attention on a single point: the breath at the tip of the nose.",
      "Let the attention grow steady and unbroken, breath after breath.",
      "Now widen your awareness to include sensations arising and passing.",
      "Notice how each sensation appears... changes... and dissolves.",
      "Observe thoughts as clouds moving across an open, spacious sky.",
      "Rest as the sky itself, aware and unshaken by whatever passes through.",
      "There is nothing to grasp, and nothing to push away. Simply awareness.",
      "Remain here in open stillness, as long as you wish.",
    ],
  },
  "meditation-mastery-living-meditation": {
    label: "5-minute guided practice",
    lines: [
      "Sit as though you have all the time in the world, because in this moment, you do.",
      "Let the breath breathe itself. There is nothing for you to do.",
      "Feel the quiet aliveness present in the whole body at once.",
      "Whatever arises, sound, sensation, thought, welcome it with equanimity.",
      "Meet each experience with a soft, open heart, and let it be.",
      "Sense the awareness that holds all of it, boundless and at peace.",
      "Let a gentle warmth arise: may I be well; may all beings be well.",
      "Carry this presence with you as you move back into your day.",
      "Rest in the knowing that this peace is always here, waiting within.",
    ],
  },
};

BLOG_POSTS.forEach((p) => {
  if (MED_PARTS[p.slug]) p.part = MED_PARTS[p.slug];
  if (MED_SCRIPTS[p.slug]) p.audio = MED_SCRIPTS[p.slug];
});

// Ordered meditation series (Part 1 -> 4)
export function getMeditationSeries() {
  return BLOG_POSTS.filter((p) => p.category === "meditation" && p.part).sort(
    (a, b) => a.part - b.part
  );
}

// Prev/next navigation within the meditation series for a given slug
export function getSeriesNav(slug) {
  const series = getMeditationSeries();
  const idx = series.findIndex((p) => p.slug === slug);
  if (idx < 0) return null;
  return {
    total: series.length,
    current: series[idx].part,
    prev: idx > 0 ? series[idx - 1] : null,
    next: idx < series.length - 1 ? series[idx + 1] : null,
  };
}

// All unique tags (sorted)
export const ALL_TAGS = Array.from(
  new Set(BLOG_POSTS.flatMap((p) => p.tags))
).sort((a, b) => a.localeCompare(b));

export function getPostsByCategory(categoryId) {
  if (categoryId === "all") return BLOG_POSTS;
  return BLOG_POSTS.filter((p) => p.category === categoryId);
}

export function getPostBySlug(slug) {
  return BLOG_POSTS.find((p) => p.slug === slug);
}

// Filter by category + free-text query + active tag
export function filterPosts({ category = "all", query = "", tag = null } = {}) {
  const q = query.trim().toLowerCase();
  return BLOG_POSTS.filter((p) => {
    if (category !== "all" && p.category !== category) return false;
    if (tag && !p.tags.includes(tag)) return false;
    if (q) {
      const haystack = (
        p.title +
        " " +
        p.excerpt +
        " " +
        p.tags.join(" ") +
        " " +
        CATEGORY_META[p.category].label
      ).toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
}
